/**
 * Small phones (a Pro with Display Zoom on, an SE, a mini) can't show the
 * countdown at its reference sizes without scrolling, which pushes the dial
 * off screen. This steps the screen through denser layouts until `scroller`
 * stops overflowing. Step n adds the classes fit-1 .. fit-n to `host`, so a
 * look's CSS for `.fit-2` also applies at 3 and 4: 1 is spacing only, 2
 * shrinks type and the numbers, 3 drops the sub line, 4 is the densest.
 * If even that overflows, data-scroll is set and the look docks its dial to
 * the bottom edge so it stays in reach.
 *
 * `relayout` re-runs anything sized in JS (the hero numeral) after each step.
 * Copy changes call refit(false), which only ever gets denser, so tapping
 * through levels can't make the layout bounce back and forth. A resize of
 * the scroller itself (rotation, the lab's device sizes) starts over.
 */
export const MAX_FIT = 4;

export function createFitter(scroller: HTMLElement, host: HTMLElement, relayout: () => void) {
  let level = 0;
  const overflowing = () => scroller.scrollHeight > scroller.clientHeight + 1;

  function apply(n: number) {
    level = n;
    for (let i = 1; i <= MAX_FIT; i++) host.classList.toggle(`fit-${i}`, i <= n);
    relayout();
  }

  function refit(reset = true) {
    delete host.dataset.scroll;
    apply(reset ? 0 : level);
    while (overflowing() && level < MAX_FIT) apply(level + 1);
    if (overflowing()) host.dataset.scroll = "";
  }

  // Web fonts change line wraps, so measure again once they're in.
  document.fonts?.ready.then(() => {
    if (scroller.isConnected) refit();
  });

  let lastW = 0;
  let lastH = 0;
  const observer = new ResizeObserver(([entry]) => {
    const { width, height } = entry.contentRect;
    if (width === lastW && height === lastH) return;
    const first = lastW === 0;
    lastW = width;
    lastH = height;
    if (!first) refit();
  });
  observer.observe(scroller);

  return { refit, destroy: () => observer.disconnect() };
}

/** Reads a px length custom property, e.g. the hero numeral's max size for the current fit. */
export function cssPx(el: HTMLElement, prop: string, fallback: number): number {
  const v = parseFloat(getComputedStyle(el).getPropertyValue(prop));
  return Number.isFinite(v) && v > 0 ? v : fallback;
}
